// React
import { useContext, useEffect, useRef } from 'react'
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useRouteError,
  useRouteLoaderData,
  useLocation,
  useMatches,
} from '@remix-run/react'
import { redirect } from '@remix-run/node'

// Internal packages and functions
import { getProductOverrides, getSite } from '~/sanity'
import {
  getSession,
  commitSession,
  getPopularSearches,
  fetchBrendaUser,
  fetchBasket,
  csrfCookie,
} from '~/.server'
import {
  Notification,
  Header,
  HeaderSimple,
  Footer,
  Button,
  ErrorResponse,
  MaintenancePage,
  USPSlider,
} from '~/components'
import {
  NonceContext,
  getBankHolidays,
  checkRedirects,
  applySnowplowUserId,
  PRODUCT_DOCUMENTS_PATH,
} from './utils'
import { useCustomScripts, useHeaderSimple, useUspBlock } from '~/hooks'

// External packages and functions
import { trackPageView } from '@snowplow/browser-tracker'
import { captureRemixErrorBoundaryError, withSentry } from '@sentry/remix'

// Styles
import styles from '~/tailwind.css?url'

// Type declarations
import type { ReactNode } from 'react'
import type { LinksFunction, LoaderFunction } from '@remix-run/node'
import type { ShouldRevalidateFunction } from '@remix-run/react'
import type { RootDataProps, UserAddressProps } from '~/types'

// Head files
export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: styles },
  { rel: 'stylesheet', href: 'https://use.typekit.net/ubt6xee.css' },
  {
    rel: 'icon',
    href: '/favicon.png',
    type: 'image/png',
  },
  {
    rel: 'entitymap',
    type: 'application/json',
    href: '/entitymap.json',
  },
]

/*
  Searching or paging product documents only changes that page's results, so the root loader
  (session, site settings, basket, bank holidays) is skipped. A changed basket_id still reloads it,
  since the loader reads that from the query string.
*/
export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
  formMethod,
  defaultShouldRevalidate,
}) => {
  const isDocumentsSearch =
    !formMethod &&
    currentUrl.pathname === PRODUCT_DOCUMENTS_PATH &&
    nextUrl.pathname === PRODUCT_DOCUMENTS_PATH &&
    currentUrl.search !== nextUrl.search &&
    currentUrl.searchParams.get('basket_id') ===
      nextUrl.searchParams.get('basket_id')

  if (isDocumentsSearch) return false

  return defaultShouldRevalidate
}

// Loader data
export const loader: LoaderFunction = async ({ request }) => {
  // Global env variables
  const { pathname, search } = new URL(request.url)
  const env = process.env

  // Session & cookie data
  const cookieHeader = request.headers.get('Cookie')
  const session = await getSession(cookieHeader)

  // CSRF token — reuse the existing cookie value or mint a new one. Echoed to
  // the client (via loader data) and re-set as an httpOnly cookie below so the
  // server can match the two halves on protected form POSTs.
  const existingCsrf = (await csrfCookie.parse(cookieHeader)) as string | null
  const csrfToken = existingCsrf ?? crypto.randomUUID()

  // User entered address
  const address = session.get('address') ?? null

  // Brenda (low-auth) user data
  const lowAuthSession = (await getSession(request.headers.get('Cookie'))).get(
    'lowAuthSession'
  ) as string | undefined
  const user = lowAuthSession
    ? await fetchBrendaUser(request).catch(() => null)
    : null

  // Fetch global data
  const site = await getSite({
    dataset: env.ENVIRONMENT,
    projectId: env.SANITY_PROJECT,
  })

  // Sitewide redirects
  const hasRedirect = checkRedirects({
    pathname,
    searchQuery: search,
    redirects: site?.settings?.redirects,
  })

  if (hasRedirect?.slug) {
    return redirect(
      hasRedirect.slug,
      hasRedirect?.code ? Number(hasRedirect.code) : 301
    )
  }

  // Session flash data
  const notification = session.has('notification')
    ? session.get('notification')
    : null

  // Currently only doing england/wales bank holidays for PDP
  const bankHolidays = await getBankHolidays('england-and-wales')

  // Cart data for the header total
  const searchParams = new URLSearchParams(search)
  const basketId = searchParams.get('basket_id')
  const basket = await fetchBasket(request, basketId ?? undefined)

  // Handle cart token
  const basketToken =
    basket?.data?.attributes?.token ?? basket?.data?.attributes?.meta?.token

  if (basketToken) {
    session.set('cartToken', basketToken)
  }

  // Get most popular searches
  const popularSearches = await getPopularSearches()
    .then(data => data.body.popularSearches ?? [])
    .catch(() => [])

  // Get most popular searches
  const productOverrides = await getProductOverrides({
    dataset: env.ENVIRONMENT,
    projectId: env.SANITY_PROJECT,
  })
    .then(data => data ?? [])
    .catch(() => [])

  // Parse the stored address defensively — it's a JSON string set by
  // api.get-prices, but a malformed/legacy value must not throw here and 500
  // the root loader, which runs on every route.
  let userAddress: UserAddressProps | null = null

  if (typeof address === 'string') {
    try {
      userAddress = JSON.parse(address)
    } catch {
      userAddress = null
    }
  }

  return Response.json(
    {
      site: site,
      cart: basket,
      user: user,
      userAddress: userAddress,
      notification: notification,
      bankHolidays: bankHolidays,
      popularSearches: popularSearches,
      productOverrides: productOverrides,
      csrfToken: csrfToken,
      env: {
        ENVIRONMENT: env?.ENVIRONMENT,
        SANITY_PROJECT: env?.SANITY_PROJECT,
        SITE_URL: env?.SITE_URL,
        BRENDA_API_URL: env?.BRENDA_API_URL,
        GOOGLE_MAPS_API_KEY: env?.GOOGLE_MAPS_API_KEY,
        GETADDRESS_IO_API_KEY: env?.GETADDRESS_IO_API_KEY,
        SNOWPLOW_URL: env?.SNOWPLOW_URL,
        SNOWPLOW_APPID: env?.SNOWPLOW_APPID,
        SNOWPLOW_DEBUG: env?.SNOWPLOW_DEBUG,
        VITE_STRIPE_PUBLIC_KEY: env?.VITE_STRIPE_PUBLIC_KEY,
        VITE_RECAPTCHA_SITE_KEY: env?.VITE_RECAPTCHA_SITE_KEY,
        DISABLE_EXTERNAL_SCRIPTS: env?.DISABLE_EXTERNAL_SCRIPTS ?? 'false',
        ENVIRONMENT_NAME: env?.ENVIRONMENT_NAME ?? 'production',
        SENTRY_DSN: env?.SENTRY_DSN,
        SENTRY_DEBUG: env?.SENTRY_DEBUG,
        MAINTENANCE_MODE: env?.MAINTENANCE_MODE ?? 'false',
        DEBUG_PDP_FLOW: env?.DEBUG_PDP_FLOW ?? 'false',
        DEBUG_CHECKOUT_FE: env?.DEBUG_CHECKOUT_FE ?? 'false',
      },
    },
    {
      headers: [
        ['Set-Cookie', await commitSession(session)],
        ['Set-Cookie', await csrfCookie.serialize(csrfToken)],
      ],
    }
  )
}

export function ErrorBoundary() {
  const error = useRouteError()

  captureRemixErrorBoundaryError(error)

  if (isRouteErrorResponse(error)) {
    return (
      <main>
        <div className="container my-8">
          <ErrorResponse message={error.status + ' ' + error.statusText}>
            <p className="text-lg">{error.data}</p>
          </ErrorResponse>
          <div className="text-center">
            <Button
              title="Back to home"
              to="/"
              reloadDocument
            />
          </div>
        </div>
      </main>
    )
  } else if (error instanceof Error) {
    return (
      <main>
        <div className="container my-8">
          <ErrorResponse message="Error">
            <p className="text-lg">{error.message}</p>
          </ErrorResponse>
          <div className="text-center">
            <Button
              title="Back to home"
              to="/"
              reloadDocument
            />
          </div>
        </div>
      </main>
    )
  } else {
    return (
      <main>
        <div className="container my-8">
          <ErrorResponse message="Unknown Error">
            <p className="text-lg">
              If this problem persists, please contact{' '}
              <a
                href="mailto:onlinesupport@hss.com"
                className="underline"
              >
                onlinesupport@hss.com
              </a>
            </p>
          </ErrorResponse>
        </div>
      </main>
    )
  }
}

interface OwnLayoutHandle {
  ownLayout?: { bodyClassName?: string }
}

// Main export
export function Layout({ children }: { children: ReactNode }) {
  const { site, cart, env, user } = useRouteLoaderData('root') as RootDataProps
  const location = useLocation()
  const matches = useMatches()

  // Prefer the cart from a child route (basket/checkout) when available
  // — those routes run revalidation and may have auto-corrected prices
  // that the root loader's parallel fetch missed.
  const childCart = matches
    .filter(m => m.id !== 'root' && (m.data as Record<string, unknown>)?.cart)
    .at(-1)?.data as { cart?: typeof cart } | undefined
  const headerCart = childCart?.cart ?? cart
  const nonce = useContext(NonceContext)

  // Determine whether the USP block should show on this route
  const showUspBlock = useUspBlock()

  // Pull USP items from global Sanity data.
  // Adjust to the real path once you confirm it.
  const uspItems = site?.global?.uspSlider ?? []

  const showHeaderSimple = useHeaderSimple()

  // A route can bring its own header and footer in place of the site's, through its handle
  const ownLayout = matches
    .map(m => (m.handle as OwnLayoutHandle | undefined)?.ownLayout)
    .find(Boolean)

  // Init custom scripts
  useCustomScripts({
    disable: env?.DISABLE_EXTERNAL_SCRIPTS == 'true' ? true : false,
    gtmId: site?.settings?.gtmId,
    nonce: nonce,
    recaptchaSiteKey: env?.VITE_RECAPTCHA_SITE_KEY,
  })

  // Track snowplow page views
  const prevUserIdRef = useRef<string | null>(null)
  const lastTrackedRef = useRef<string | null>(null)

  // stable primitive
  const userId: string | null | undefined =
    user === undefined ? undefined : user?.id != null ? String(user.id) : null

  // Apply identity when the ID changes
  useEffect(() => {
    const { nextUserId } = applySnowplowUserId(userId, prevUserIdRef.current)
    prevUserIdRef.current = nextUserId
  }, [userId])

  // Track page views once per navigation (not per user)
  useEffect(() => {
    const params = new URLSearchParams(location.search)

    // Live search on product documents rewrites ?query= as people type; that isn't a new page view
    if (location.pathname === PRODUCT_DOCUMENTS_PATH) params.delete('query')

    const dedupeKey = `${location.pathname}?${params}${location.hash}`
    if (lastTrackedRef.current === dedupeKey) return
    lastTrackedRef.current = dedupeKey

    trackPageView()
  }, [location.pathname, location.search, location.hash])

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, minimum-scale=1"
        />
        <Links />
        <Meta />
      </head>
      <body
        className={ownLayout?.bodyClassName ?? 'bg-neutral-50 text-gray-900'}
      >
        {env?.MAINTENANCE_MODE === 'true' ? (
          <main>
            <MaintenancePage />
          </main>
        ) : (
          <>
            {!ownLayout &&
              (showHeaderSimple ? (
                <HeaderSimple />
              ) : (
                <Header
                  basketTotal={headerCart?.data?.attributes?.prices?.total}
                  uspBlockShown={showUspBlock}
                />
              ))}
            {!ownLayout && showUspBlock && uspItems.length > 0 && (
              <USPSlider items={uspItems} />
            )}
            {children}
            {!ownLayout && <Footer />}
            <Notification />
          </>
        )}
        <ScrollRestoration />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
        />
        <Scripts />
      </body>
    </html>
  )
}

function App() {
  return <Outlet />
}

export default withSentry(App)
